
import React from 'react';
import { motion } from 'framer-motion';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { HelpCircle, Users, BookOpen, ArrowRightCircle, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

const FAQPage = () => {
  const fadeIn = (delay = 0, y = 20) => ({
    hidden: { opacity: 0, y: y, scale: 0.98 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.7, delay, ease: [0.6, -0.05, 0.01, 0.99] } },
  });

  const faqsParents = [
    {
      question: "Is Classbuddy écht veilig en anoniem voor mijn kind?",
      answer: "Ja, 100%. Veiligheid en anonimiteit zijn de kern van Classbuddy. Kinderen delen geen persoonlijke info, gesprekken worden niet opgeslagen en alle data voor scholen is volledig geanonimiseerd. We voldoen strikt aan de AVG."
    },
    {
      question: "Kan ik meelezen met de gesprekken van mijn kind?",
      answer: "Nee, de gesprekken zijn strikt privé tussen het kind en de AI-buddy. Dit is cruciaal voor de vertrouwensband en om een veilige, oordeelvrije ruimte te bieden. Scholen zien alleen algemene, anonieme thema's."
    },
    {
      question: "Voor welke leeftijdscategorie is Classbuddy het meest geschikt?",
      answer: "Classbuddy is primair ontworpen voor kinderen in het basisonderwijs (ongeveer 6-12 jaar). De taal, thema's en interacties zijn afgestemd op hun belevingswereld en ontwikkelingsfase."
    },
    {
      question: "Wat als mijn kind iets ernstigs deelt met de buddy?",
      answer: "Onze AI-buddy's zijn getraind om signalen van ernstige zorgen te herkennen. In zulke gevallen zal de buddy het kind altijd aanmoedigen om met een vertrouwd persoon (ouder, leerkracht) te praten en kan het, indien zo ingesteld door de school, een anonieme melding genereren voor de aangewezen zorgcoördinator op school."
    }
  ];

  const faqsSchools = [
    {
      question: "Hoe eenvoudig is de implementatie van Classbuddy op school?",
      answer: "Zeer eenvoudig! Classbuddy is een webapp, dus geen ingewikkelde installaties. We bieden uitgebreide onboarding, training voor leerkrachten en doorlopende support. Binnen no-time zijn jullie up and running."
    },
    {
      question: "Wat zijn de kosten en is er een proefperiode?",
      answer: "De kosten zijn afhankelijk van de schoolgrootte en specifieke wensen. We bieden flexibele pakketten. Neem contact op voor een offerte op maat. We bieden vaak ook de mogelijkheid voor een proefperiode!"
    },
    {
      question: "Werkt Classbuddy op de devices die wij op school gebruiken (tablets, chromebooks)?",
      answer: "Absoluut. Classbuddy is een responsieve webapplicatie, geoptimaliseerd voor desktops, laptops, tablets en Chromebooks. Een stabiele internetverbinding is het enige wat nodig is."
    },
    {
      question: "Hoe waarborgt Classbuddy de privacy van leerlinggegevens richting de school?",
      answer: "Alle inzichten die scholen ontvangen zijn volledig geanonimiseerd en geaggregeerd. Het is onmogelijk om data te herleiden naar individuele leerlingen. We tonen trends en thema's, geen persoonlijke details."
    },
    {
      question: "Bieden jullie ook trainingen aan voor ons team?",
      answer: "Ja! Classbuddy biedt pedagogische trainingen aan die aansluiten op de thema's van onze buddy's, zoals pesten, emotieregulatie en groepsvorming. Deze trainingen zijn praktisch, actueel en gericht op signaleren, begeleiden en versterken van sociaal-emotioneel welzijn in de klas. Interesse? Mail ons via info@classbuddy.nl"
    }
  ];

  return (
    <div className="container mx-auto px-4 section-padding overflow-hidden">
      <motion.div
        initial="hidden"
        animate="visible"
        className="text-center mb-16"
      >
        <motion.div variants={fadeIn(0,30)} className="flex justify-center mb-8">
          <HelpCircle size={72} className="text-cm-yellow" />
        </motion.div>
        <motion.h1
          variants={fadeIn(0.1, 30)}
          className="page-title"
        >
          Veelgestelde Vragen - <span className="text-gradient-pink-blue font-medium">Uw Gids</span>
        </motion.h1>
        <motion.p
          variants={fadeIn(0.2, 20)}
          className="page-subtitle"
        >
          Heeft u vragen over Classbuddy? Hier vindt u antwoorden op de meest gestelde vragen van zowel ouders als scholen. We streven naar volledige transparantie en duidelijkheid.
        </motion.p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
        <motion.div variants={fadeIn(0.3, 20)} initial="hidden" animate="visible">
          <div className="flex items-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-cm-pink-light to-cm-pink/70 rounded-full flex items-center justify-center shadow-lg mr-4">
              <Users size={36} className="text-white" />
            </div>
            <h2 className="text-4xl font-heading font-medium text-cm-blue-dark">Voor Ouders</h2>
          </div>
          <Accordion type="single" collapsible className="w-full space-y-4">
            {faqsParents.map((faq, index) => (
              <motion.div key={`parent-${index}`} variants={fadeIn(0.4 + index * 0.05, 10)} initial="hidden" animate="visible">
                <AccordionItem value={`item-p-${index}`} className="glassmorphism-card p-1 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                  <AccordionTrigger className="text-left hover:no-underline text-lg px-6 py-4 font-medium text-cm-blue-dark">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-cm-gray pt-1 pb-4 px-6 text-md leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              </motion.div>
            ))}
          </Accordion>
        </motion.div>

        <motion.div variants={fadeIn(0.35, 20)} initial="hidden" animate="visible">
          <div className="flex items-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-cm-green-light to-cm-green/70 rounded-full flex items-center justify-center shadow-lg mr-4">
                <BookOpen size={36} className="text-white" />
            </div>
            <h2 className="text-4xl font-heading font-medium text-cm-blue-dark">Voor Scholen</h2>
          </div>
          <Accordion type="single" collapsible className="w-full space-y-4">
            {faqsSchools.map((faq, index) => (
              <motion.div key={`school-${index}`} variants={fadeIn(0.45 + index * 0.05, 10)} initial="hidden" animate="visible">
                <AccordionItem value={`item-s-${index}`} className="glassmorphism-card p-1 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                  <AccordionTrigger className="text-left hover:no-underline text-lg px-6 py-4 font-medium text-cm-blue-dark">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-cm-gray pt-1 pb-4 px-6 text-md leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              </motion.div>
            ))}
          </Accordion>
        </motion.div>
      </div>

      <motion.div
        variants={fadeIn(0.8, 20)}
        initial="hidden"
        animate="visible"
        className="mt-20 p-10 bg-gradient-to-r from-cm-blue-light/70 via-cm-yellow-light/70 to-cm-pink-light/70 rounded-3xl shadow-2xl text-center"
      >
        <CheckCircle size={56} className="text-cm-blue mx-auto mb-6" />
        <h2 className="text-4xl font-heading font-medium text-cm-blue-dark mb-5">Nog steeds een vraag?</h2>
        <p className="text-lg text-cm-gray mb-8 max-w-2xl mx-auto leading-relaxed">
          Staat uw specifieke vraag er niet tussen of wilt u meer gedetailleerde informatie? Aarzel niet om contact met ons op te nemen. Ons team staat klaar om u te helpen!
        </p>
        <Button asChild size="lg" className="bg-gradient-to-r from-cm-pink to-cm-blue hover:from-cm-pink-dark hover:to-cm-blue-dark text-white font-medium text-lg px-10 py-7 rounded-xl shadow-xl transform hover:scale-105 transition-all duration-300 group">
          <Link to="/contact">
            Neem Contact Op <ArrowRightCircle className="ml-2.5 h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </Button>
      </motion.div>
    </div>
  );
};

export default FAQPage;
